import type { ConflictPolicy, FileManifestEntry, LocalSource } from './types';

export interface SourceSelection {
  sources: LocalSource[];
  directories: string[];
}

export function mergeSelections(current: SourceSelection, added: SourceSelection): SourceSelection {
  const sources = [...current.sources];
  const byPath = new Map(sources.map((source) => [source.manifest.relativePath, source]));
  for (const source of added.sources) {
    const existing = byPath.get(source.manifest.relativePath);
    if (existing) {
      if (existing.manifest.size === source.manifest.size && existing.manifest.lastModified === source.manifest.lastModified) continue;
      throw new Error(`Two selected files would use the same path: ${source.manifest.relativePath}`);
    }
    byPath.set(source.manifest.relativePath, source);
    sources.push(source);
  }
  return { sources, directories: [...new Set([...current.directories, ...added.directories])] };
}

export function removeTopLevel(selection: SourceSelection, topLevelName: string): SourceSelection {
  const isWithin = (path: string) => path.split('/')[0] === topLevelName;
  return {
    sources: selection.sources.filter((source) => !isWithin(source.manifest.relativePath)),
    directories: selection.directories.filter((path) => !isWithin(path)),
  };
}

export function sanitizeRelativePath(path: string): string {
  if (/^(?:[A-Za-z]:[\\/]|[\\/])/u.test(path)) throw new Error(`Unsafe file path: ${path}`);
  const normalized = path.replaceAll('\\', '/').replace(/^\/+|\/+$/gu, '');
  const segments = normalized.split('/');
  if (!normalized || segments.some((segment) => !segment || segment === '.' || segment === '..' || segment.includes('\0'))) {
    throw new Error(`Unsafe file path: ${path}`);
  }
  return segments.join('/');
}

export function createManifestEntry(file: File, relativePath: string): FileManifestEntry {
  return {
    id: crypto.randomUUID(),
    relativePath: sanitizeRelativePath(relativePath),
    size: file.size,
    lastModified: file.lastModified,
    type: file.type,
  };
}

async function enumerateHandle(handle: FileSystemHandle, prefix = ''): Promise<SourceSelection> {
  if (handle.kind === 'file') {
    const fileHandle = handle as FileSystemFileHandle;
    const file = await fileHandle.getFile();
    const relativePath = prefix ? `${prefix}/${file.name}` : file.name;
    return { sources: [{ file, handle: fileHandle, manifest: createManifestEntry(file, relativePath) }], directories: [] };
  }

  const directory = handle as FileSystemDirectoryHandle;
  const nextPrefix = prefix ? `${prefix}/${directory.name}` : directory.name;
  const selection: SourceSelection = { sources: [], directories: [sanitizeRelativePath(nextPrefix)] };
  for await (const [, child] of directory.entries()) {
    const nested = await enumerateHandle(child, nextPrefix);
    selection.sources.push(...nested.sources);
    selection.directories.push(...nested.directories);
  }
  return selection;
}

export async function selectionFromHandles(handles: FileSystemHandle[]): Promise<SourceSelection> {
  const selections = await Promise.all(handles.map((handle) => enumerateHandle(handle)));
  const sources = selections.flatMap((selection) => selection.sources);
  const directories = [...new Set(selections.flatMap((selection) => selection.directories))];
  const paths = new Set<string>();
  for (const source of sources) {
    if (paths.has(source.manifest.relativePath)) throw new Error(`Duplicate path: ${source.manifest.relativePath}`);
    paths.add(source.manifest.relativePath);
  }
  return { sources, directories };
}

export function selectionFromFileList(files: FileList | File[]): SourceSelection {
  const sources = Array.from(files).map((file) => ({
    file,
    manifest: createManifestEntry(file, file.webkitRelativePath || file.name),
  }));
  const paths = new Set<string>();
  for (const source of sources) {
    if (paths.has(source.manifest.relativePath)) throw new Error(`Duplicate path: ${source.manifest.relativePath}`);
    paths.add(source.manifest.relativePath);
  }
  return { sources, directories: [] };
}

export async function sourcesFromHandles(handles: FileSystemHandle[]): Promise<LocalSource[]> {
  return (await selectionFromHandles(handles)).sources;
}

export async function selectionFromDrop(dataTransfer: DataTransfer): Promise<SourceSelection> {
  const handles: FileSystemHandle[] = [];
  for (const item of dataTransfer.items) {
    const handle = await item.getAsFileSystemHandle?.();
    if (handle) handles.push(handle);
  }
  if (handles.length) return selectionFromHandles(handles);
  return selectionFromFileList(dataTransfer.files);
}

export async function sourcesFromDrop(dataTransfer: DataTransfer): Promise<LocalSource[]> {
  return (await selectionFromDrop(dataTransfer)).sources;
}

export async function ensureReadPermission(handle: FileSystemFileHandle): Promise<boolean> {
  if (await handle.queryPermission({ mode: 'read' }) === 'granted') return true;
  return (await handle.requestPermission({ mode: 'read' })) === 'granted';
}

export async function ensureWritePermission(handle: FileSystemDirectoryHandle | FileSystemFileHandle): Promise<boolean> {
  if (await handle.queryPermission({ mode: 'readwrite' }) === 'granted') return true;
  return (await handle.requestPermission({ mode: 'readwrite' })) === 'granted';
}

export async function fileExists(directory: FileSystemDirectoryHandle, name: string): Promise<boolean> {
  try {
    await directory.getFileHandle(name);
    return true;
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotFoundError') return false;
    throw error;
  }
}

export function numberedName(name: string, number: number): string {
  const dot = name.lastIndexOf('.');
  if (dot <= 0) return `${name} (${number})`;
  return `${name.slice(0, dot)} (${number})${name.slice(dot)}`;
}

export async function createUniqueDestination(root: FileSystemDirectoryHandle, relativePath: string): Promise<FileSystemFileHandle> {
  const segments = sanitizeRelativePath(relativePath).split('/');
  const requestedName = segments.pop()!;
  let directory = root;
  for (const segment of segments) directory = await directory.getDirectoryHandle(segment, { create: true });
  let name = requestedName;
  let counter = 1;
  while (await fileExists(directory, name)) name = numberedName(requestedName, counter++);
  return directory.getFileHandle(name, { create: true });
}

export interface DestinationTarget {
  handle?: FileSystemFileHandle;
  skipped: boolean;
  replaced: boolean;
}

export async function destinationFileExists(root: FileSystemDirectoryHandle, relativePath: string): Promise<boolean> {
  const segments = sanitizeRelativePath(relativePath).split('/');
  const name = segments.pop()!;
  let directory = root;
  try {
    for (const segment of segments) directory = await directory.getDirectoryHandle(segment);
  } catch (error) {
    if (error instanceof DOMException && error.name === 'NotFoundError') return false;
    throw error;
  }
  return fileExists(directory, name);
}

export async function createDestination(
  root: FileSystemDirectoryHandle,
  relativePath: string,
  policy: Exclude<ConflictPolicy, 'ask'>,
): Promise<DestinationTarget> {
  const segments = sanitizeRelativePath(relativePath).split('/');
  const requestedName = segments.pop()!;
  let directory = root;
  for (const segment of segments) directory = await directory.getDirectoryHandle(segment, { create: true });
  const exists = await fileExists(directory, requestedName);
  if (!exists) return { handle: await directory.getFileHandle(requestedName, { create: true }), skipped: false, replaced: false };
  if (policy === 'skip-existing') return { skipped: true, replaced: false };
  if (policy === 'replace') return { handle: await directory.getFileHandle(requestedName), skipped: false, replaced: true };
  return { handle: await createUniqueDestination(root, relativePath), skipped: false, replaced: false };
}

export async function testDestinationWrite(directory: FileSystemDirectoryHandle): Promise<void> {
  if (!await ensureWritePermission(directory)) throw new Error('Write access to the destination was not granted.');
  const name = `.pairferry-write-test-${crypto.randomUUID()}.tmp`;
  try {
    const handle = await directory.getFileHandle(name, { create: true });
    const writable = await handle.createWritable();
    await writable.write(new Uint8Array([80, 70]));
    await writable.close();
  } catch (error) {
    throw new Error('PairFerry could not write to the selected destination. Choose another folder or check available disk space.', { cause: error });
  } finally {
    await directory.removeEntry(name).catch(() => undefined);
  }
}

export async function createDestinationDirectories(root: FileSystemDirectoryHandle, paths: string[]): Promise<void> {
  for (const path of [...paths].sort((a, b) => a.split('/').length - b.split('/').length)) {
    let directory = root;
    for (const segment of sanitizeRelativePath(path).split('/')) {
      directory = await directory.getDirectoryHandle(segment, { create: true });
    }
  }
}
